/**
 * VideoPreviewPlayer -- full video player UI for VideoGeneratorNode.
 *
 * Features:
 * - Hover play/pause/resume (pause on leave, resume on re-enter)
 * - Single-play-per-hover: plays through at most once, then stops on last frame
 * - Optional click-anywhere-on-the-frame play/pause (`toggleOnFrameClick`)
 * - Bottom row: play/pause, current time, scrub bar, total duration, optional
 *   expand (fullscreen)
 * - Single top-right overlay cluster: mute (always) + optional
 *   extraControls / download, hover-visible, then optional
 *   topTrailingControls; laid out as a row, or as a column on portrait tiles
 * - Exposes video element ref via forwardRef for external control (Play All)
 */

import {
  memo,
  useRef,
  useState,
  useCallback,
  useEffect,
  useImperativeHandle,
  forwardRef,
  type MouseEvent as ReactMouseEvent,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
} from 'react'
import { useTranslation } from 'react-i18next'
import { Maximize2 } from 'lucide-react'

import { useCanvasNodeImage } from './useCanvasNodeImage'
import { createNadDebug } from '../../../../utils/debugLog'
import {
  getVideoMutedPreference,
  setVideoMutedPreference,
} from '../../../../utils/videoMutePreference'
import { useNodeId } from 'reactflow'
import { trackInteraction } from '../../../../analytics/interactionAnalytics'
import { useOptionalProjectId } from '../../../../contexts/ProjectIdContext'

const mutePreferenceLog = createNadDebug('dw:hover-video-audio:preference')
const previewSelectionLog = createNadDebug('dw:video-preview-selection')

/**
 * Idle-release grace period (RFC-0931). After hover-out, a poster-backed player
 * keeps its <video> mounted this long before reverting to the cheap poster <img>
 * and unmounting the <video>. This bounds the number of concurrently mounted
 * <video> elements to those actively in use, so a long browsing session cannot
 * drift back to the "every hovered node stays a <video>" state. The swap is
 * pixel-identical (poster === first frame) so there is no visible flash.
 */
const IDLE_RELEASE_MS = 5000

/**
 * Crossfade duration (ms) for the poster<->video swap (RFC-0931). The poster
 * <img> is a persistent base layer; the <video> fades its opacity in over it on
 * hover and out on release. Both show the identical first frame, so the swap
 * reads as a gentle dissolve instead of a hard cut. Kept in sync with the CSS
 * transitionDuration and the unmount delay.
 */
const VIDEO_FADE_MS = 300

/** Format seconds as M:SS (e.g. 65 -> "1:05") */
export function formatTime(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return '0:00'
  const mins = Math.floor(seconds / 60)
  const secs = Math.floor(seconds % 60)
  return `${mins}:${secs.toString().padStart(2, '0')}`
}

/** Clips shorter than this show both time readouts in seconds to a tenth. */
const FRACTIONAL_DURATION_THRESHOLD_S = 30

/**
 * Format a playback readout (current time or total) for a clip of
 * `clipDuration` seconds. Clips under 30s read in tenths of a second with an
 * "s" suffix (2.9 -> "2.9s", 8 -> "8.0s") so sub-second differences between
 * short variants stay visible; longer clips keep formatTime's M:SS. Truncates
 * like formatTime, so a 29.96s clip reads "29.9s", never rounded up to 30.
 * A clipDuration of 0 means metadata has not loaded yet, so the unit is
 * unknown and the readout keeps formatTime's "0:00" placeholder.
 */
export function formatPlaybackTime(seconds: number, clipDuration: number): string {
  if (
    !Number.isFinite(clipDuration) ||
    clipDuration <= 0 ||
    clipDuration >= FRACTIONAL_DURATION_THRESHOLD_S
  ) {
    return formatTime(seconds)
  }
  if (!Number.isFinite(seconds) || seconds < 0) return '0.0s'
  // Epsilon absorbs float error (e.g. a stored 8.4 landing at 83.999…).
  const tenths = Math.floor(seconds * 10 + 1e-6)
  return `${Math.floor(tenths / 10)}.${tenths % 10}s`
}

/** Imperative handle exposed by VideoPreviewPlayer via forwardRef */
export interface VideoPreviewPlayerHandle {
  play: () => void
  pause: () => void
  /** Force-mount a poster-backed (idle) player without playing (RFC-0931). */
  activate: () => void
  readonly videoElement: HTMLVideoElement | null
}

export interface VideoPreviewPlayerProps {
  videoUrl: string
  isHovering: boolean
  /**
   * How video frames and their idle posters fit inside the preview box.
   * Defaults to "cover" to preserve existing callers. Transform nodes use
   * "contain" so portrait and square results remain fully visible.
   */
  mediaFit?: 'cover' | 'contain'
  /**
   * First-frame poster URL (RFC-0931). When present, an idle player renders a
   * cheap <img> instead of mounting the heavy <video>; the real <video> mounts
   * on hover and auto-releases back to the poster after an idle grace period so
   * mounted <video> elements don't accumulate. A missing/failed poster falls
   * back to today's always-mounted <video>, so behavior is unchanged for
   * not-yet-backfilled videos.
   */
  posterUrl?: string | null
  /**
   * Browser video preload policy. Variant grids pass "none" so opening a
   * large history panel does not eagerly fetch every video.
   */
  preload?: 'none' | 'metadata' | 'auto'
  /** Show expand (fullscreen) button at the trailing end of the bottom row */
  showExpand?: boolean
  /** Show download button in the top-right overlay cluster */
  showDownload?: boolean
  /**
   * Keep Download visible without hovering, as the stable rightmost control.
   *
   * On an exported/uploaded video node Download is the primary action and must
   * be discoverable without hovering (post 1579949463762351), while the other
   * controls stay hover-revealed. Defaults to false (everything hover-gated).
   */
  downloadAlwaysVisible?: boolean
  /**
   * RFC-1612: this node's video is being C2PA-signed for download. The download
   * button stays in place but is disabled until the signed file is ready; the
   * owning node shows the progress banner (`NodeDownloadPreparingBanner`).
   */
  downloadPreparing?: boolean
  /**
   * Extra node-specific controls injected into the same cluster, between mute
   * and download, sharing its hover gating and spacing.
   *
   * This exists so a caller with a bespoke action (VideoUploadNode's "Replace",
   * which must be a <label> wired to a file input) does not have to hand-roll a
   * SECOND absolutely-positioned cluster in the same corner. There is exactly
   * one overlay cluster per player.
   */
  extraControls?: ReactNode
  /**
   * Controls pinned to the trailing end of the top-right cluster, after
   * download.
   *
   * Unlike `extraControls`, these do NOT inherit the cluster's hover gating —
   * the child owns its own visibility. That is what the variant star needs: an
   * archived tile must show its hollow star without hovering, which the shared
   * `opacity-0 pointer-events-none` wrapper would swallow.
   */
  topTrailingControls?: ReactNode
  /**
   * Direction of the top-right cluster.
   *
   * `'row'` (default): left-to-right [mute, ...extraControls, download,
   * ...topTrailingControls].
   *
   * `'column'`: the same controls stacked down the right edge, trailing
   * controls on top — [...topTrailingControls, download, ...extraControls,
   * mute]. Portrait previews and variant tiles use it because a row of buttons
   * does not fit their width.
   */
  overlayDirection?: 'row' | 'column'
  /**
   * Let a click anywhere on the frame play/pause, not just the play button.
   *
   * Opt-in because the click is NOT consumed — it goes on bubbling to whatever
   * the caller has wired on the surrounding element. That is right for a
   * variant tile, where the same click should also select the variant, and
   * would be wrong for a caller whose parent click does something heavier.
   *
   * Controls that must not toggle playback (scrub, mute/expand/download, and
   * any `topTrailingControls`) already stop propagation, so they never reach
   * this.
   */
  toggleOnFrameClick?: boolean
  /**
   * Called before play/pause consumes a click. Owners use this to select the
   * node or variant whose visible video control was clicked.
   */
  onPreviewSelect?: () => void
  /** Called when expand button is clicked */
  onExpand?: () => void
  /** Called when download button is clicked */
  onDownload?: () => void
  /** Called when metadata exposes natural video dimensions */
  onLoadedDimensions?: (width: number, height: number) => void
  /** Called only for intrinsic video dimensions, never poster dimensions. */
  onLoadedVideoDimensions?: (width: number, height: number) => void
  /** Called when the video reaches the end */
  onEnded?: () => void
}

function appendVideoRetryParam(url: string): string {
  try {
    const parsed = new URL(url, window.location.href)
    parsed.searchParams.set('video_retry', String(Date.now()))
    return parsed.toString()
  } catch {
    const separator = url.includes('?') ? '&' : '?'
    return `${url}${separator}video_retry=${Date.now()}`
  }
}

export const VideoPreviewPlayer = memo(forwardRef<VideoPreviewPlayerHandle, VideoPreviewPlayerProps>(
  function VideoPreviewPlayer(
    {
      videoUrl,
      isHovering,
      mediaFit = 'cover',
      posterUrl,
      preload = 'metadata',
      showExpand,
      showDownload,
      downloadPreparing = false,
      downloadAlwaysVisible = false,
      extraControls,
      topTrailingControls,
      overlayDirection = 'row',
      toggleOnFrameClick = false,
      onPreviewSelect,
      onExpand,
      onDownload,
      onLoadedDimensions,
      onLoadedVideoDimensions,
      onEnded,
    },
    ref,
  ) {
  const { t } = useTranslation('canvas')
  const videoRef = useRef<HTMLVideoElement>(null)

  const [isPlaying, setIsPlaying] = useState(false)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(0)
  const [isMuted, setIsMuted] = useState(() => getVideoMutedPreference(videoUrl))
  const [hasCompletedPlayback, setHasCompletedPlayback] = useState(false)
  const [retryVideoUrl, setRetryVideoUrl] = useState<string | null>(null)
  // Idle nodes render the poster <img>; the <video> mounts on first hover and
  // stays mounted while in use, then auto-releases back to the poster after an
  // idle grace period so mounted <video> elements do not accumulate (RFC-0931).
  const [hasActivated, setHasActivated] = useState(false)
  const [posterFailed, setPosterFailed] = useState(false)
  // Crossfade bookkeeping (RFC-0931): `renderVideo` keeps the <video> in the DOM
  // through its fade-out (so it isn't yanked mid-dissolve). With no usable poster
  // the <video> must be present from the first render (no empty flash).
  const [renderVideo, setRenderVideo] = useState(() => !posterUrl)
  // Opacity is keyed to the SOURCE whose first frame is decoded, not a bare
  // boolean: `videoOpaque` is true only when the currently-mounted `playbackUrl`
  // is the one that reported `loadeddata`. This makes source changes / retries
  // transparent-by-construction (a fresh `playbackUrl` no longer matches, so the
  // remounted <video> starts hidden until IT decodes a frame) and stops a late
  // `loadeddata` from re-showing a video that is already fading out.
  const [opaquePlaybackUrl, setOpaquePlaybackUrl] = useState<string | null>(null)

  const manualPauseRef = useRef(false)
  const isDraggingRef = useRef(false)
  const playbackUrl = retryVideoUrl ?? videoUrl
  const videoOpaque = opaquePlaybackUrl === playbackUrl
  const mediaFitClass = mediaFit === 'contain' ? 'object-contain' : 'object-cover'
  // Mount the real <video> when interacting, once activated, or when there is
  // no usable poster (un-backfilled / 404) — the last case preserves today's
  // always-mounted behavior with no regression.
  const usePoster = !!posterUrl && !posterFailed
  const mountVideo = isHovering || hasActivated || !usePoster

  // Video posters are full-resolution JPEGs (getVideoPosterUrl emits no width,
  // and the API ignores `w` anyway), and video_generator is by far the densest
  // node type on real projects — 117 of 179 cards on the canvas from Workplace
  // post 1596425285448102. Queue them behind the shared canvas image pool.
  //
  // NB: this gates only the <img> src, NOT `usePoster`. `mountVideo` keys off
  // `usePoster`, so nulling it while queued would mount a real <video> per
  // card — strictly worse than the empty box we are fixing.
  const posterHostRef = useRef<HTMLDivElement | null>(null)
  const { displayUrl: gatedPosterUrl, onMediaSettled: onPosterSettled } = useCanvasNodeImage(
    usePoster ? posterUrl : null,
    posterHostRef,
  )

  const stopParentDrag = useCallback((e: ReactMouseEvent | ReactPointerEvent) => {
    e.stopPropagation()
  }, [])

  useEffect(() => {
    setRetryVideoUrl(null)
    setIsPlaying(false)
    setCurrentTime(0)
    setDuration(0)
    setIsMuted(getVideoMutedPreference(videoUrl))
    setHasCompletedPlayback(false)
    setHasActivated(false)
    // New source → hide until the fresh <video> decodes its own first frame.
    // (Identity also guards this, since `opaquePlaybackUrl` still holds the old
    // URL; clearing it is belt-and-suspenders and keeps intent explicit.)
    setOpaquePlaybackUrl(null)
  }, [videoUrl])

  // Reset poster-failed when the poster URL changes (e.g. after backfill).
  useEffect(() => {
    setPosterFailed(false)
  }, [posterUrl])

  // Activate on hover so the <video> mounts. It stays mounted while hovering or
  // playing; see the idle-release effect below for when it reverts to poster.
  useEffect(() => {
    if (isHovering) setHasActivated(true)
  }, [isHovering])

  // Idle-release: once a poster-backed player is neither hovered nor playing,
  // revert to the poster <img> after IDLE_RELEASE_MS and unmount the <video>,
  // so mounted <video> elements don't accumulate across a browsing session
  // (RFC-0931). Any hover/play in the interim cancels the pending release (the
  // deps change re-runs cleanup). No-op when there's no usable poster (the
  // <video> must stay mounted) or when already idle.
  useEffect(() => {
    if (!usePoster || !hasActivated || isHovering || isPlaying) return
    const timer = window.setTimeout(() => setHasActivated(false), IDLE_RELEASE_MS)
    return () => window.clearTimeout(timer)
  }, [usePoster, hasActivated, isHovering, isPlaying])

  // Crossfade lifecycle (RFC-0931): mount the <video> as soon as it's wanted;
  // on release, fade it out first and unmount only after the dissolve completes,
  // revealing the persistent poster underneath.
  useEffect(() => {
    if (mountVideo) {
      setRenderVideo(true)
      return
    }
    // Release: fade the <video> out (drop opacity now), unmount after the fade.
    setOpaquePlaybackUrl(null)
    const timer = window.setTimeout(() => setRenderVideo(false), VIDEO_FADE_MS)
    return () => window.clearTimeout(timer)
  }, [mountVideo])

  // The video normally fades in via `onLoadedData` (see handleLoadedData). This
  // effect covers the case where the current `playbackUrl` was ALREADY buffered
  // before the listener could observe a `loadeddata` (e.g. a keyed remount that
  // reuses the browser cache — on source change / retry / variant switch): if it
  // already has a decoded first frame, reveal THIS source right away instead of
  // waiting for a `loadeddata` that may never fire again. Depends on
  // `playbackUrl` so it re-checks the freshly-keyed element, not a stale one.
  useEffect(() => {
    if (!renderVideo || !mountVideo) return
    const video = videoRef.current
    if (video && video.readyState >= 2 /* HAVE_CURRENT_DATA */) {
      setOpaquePlaybackUrl(playbackUrl)
    }
  }, [renderVideo, mountVideo, playbackUrl])

  // Deferred play for a poster-backed player: "Play All" (VideoVariantsTabContent)
  // may call play() while the <video> is still unmounted (idle poster). We set
  // this flag, force-activate the mount, and start playback once it mounts.
  const pendingPlayRef = useRef(false)

  /* -- Expose imperative handle for external control -- */
  useImperativeHandle(ref, () => ({
    play: () => {
      const video = videoRef.current
      if (!video) {
        // Poster-backed & unmounted → mount now, play on mount (below).
        pendingPlayRef.current = true
        setHasActivated(true)
        return
      }
      if (hasCompletedPlayback) {
        video.currentTime = 0
        setHasCompletedPlayback(false)
      }
      video.play().catch(() => {})
      setIsPlaying(true)
    },
    pause: () => {
      pendingPlayRef.current = false
      const video = videoRef.current
      if (!video) return
      video.pause()
      setIsPlaying(false)
    },
    activate: () => setHasActivated(true),
    get videoElement() {
      return videoRef.current
    },
  }), [hasCompletedPlayback])

  // Play a pending "Play All" request once the poster-backed <video> mounts.
  // Keyed on renderVideo (DOM presence) so videoRef is set when this runs.
  useEffect(() => {
    if (!renderVideo || !pendingPlayRef.current) return
    const video = videoRef.current
    if (!video) return
    pendingPlayRef.current = false
    video.currentTime = 0
    video.play().catch(() => {})
    setIsPlaying(true)
  }, [renderVideo])

  /* -- Hover play/pause/resume -- */
  // Depends on renderVideo too: on first hover the <video> mounts a tick later,
  // so re-run once it's in the DOM to actually start playback.
  useEffect(() => {
    const video = videoRef.current
    if (!video) return

    if (isHovering) {
      let cancelled = false
      // Re-read on every hover so another mounted player for this same video
      // picks up the user's latest browser-local choice before playback starts.
      const preferredMuted = getVideoMutedPreference(videoUrl)
      video.muted = preferredMuted
      setIsMuted(preferredMuted)
      mutePreferenceLog({
        event: 'hover-playback-mute-resolved',
        surface: 'canvas-preview',
        muted: preferredMuted,
      })
      if (hasCompletedPlayback) {
        video.currentTime = 0
        setHasCompletedPlayback(false)
      }
      manualPauseRef.current = false
      video.play().then(
        () => {
          if (!cancelled && !video.paused) setIsPlaying(true)
        },
        (error) => {
          if (cancelled) return
          setIsPlaying(false)
          mutePreferenceLog({
            event: 'hover-playback-rejected',
            surface: 'canvas-preview',
            reason: error instanceof Error ? `${error.name}: ${error.message}` : String(error),
          })
        },
      )
      return () => {
        cancelled = true
      }
    } else {
      video.pause()
      setIsPlaying(false)
    }
  }, [isHovering, renderVideo, videoUrl]) // eslint-disable-line react-hooks/exhaustive-deps

  /* -- Video event handlers -- */
  const handleTimeUpdate = useCallback(() => {
    const video = videoRef.current
    if (!video || isDraggingRef.current) return
    setCurrentTime(video.currentTime)
  }, [])

  // `timeupdate` only fires every ~250ms, which makes the scrub bar and time
  // readout visibly step during playback. While playing, sample currentTime
  // every animation frame instead; `timeupdate` still covers seeks and pauses.
  useEffect(() => {
    if (!isPlaying || !renderVideo) return
    let frameId = requestAnimationFrame(function tick() {
      const video = videoRef.current
      if (video && !isDraggingRef.current) setCurrentTime(video.currentTime)
      frameId = requestAnimationFrame(tick)
    })
    return () => cancelAnimationFrame(frameId)
  }, [isPlaying, renderVideo])

  const handleLoadedMetadata = useCallback(() => {
    const video = videoRef.current
    if (!video) return
    setDuration(video.duration)
    onLoadedDimensions?.(video.videoWidth, video.videoHeight)
    onLoadedVideoDimensions?.(video.videoWidth, video.videoHeight)
  }, [onLoadedDimensions, onLoadedVideoDimensions])

  // Fade the <video> in only once it has a decoded first frame to paint
  // (`loadeddata` → readyState >= HAVE_CURRENT_DATA). The <video> carries no
  // poster attribute (see the render below), so becoming opaque before it has a
  // frame would paint an empty/black surface over the persistent poster <img>.
  // Gating on the first decoded frame keeps the base <img> covering the whole
  // buffering window, so there is no flash even under reduced-motion,
  // preload="none", or a variant switch while hovered. Record the source that
  // decoded (not a bare flag) and ignore late events once the player is no
  // longer wanted, so a `loadeddata` arriving mid-fade-out can't re-reveal a
  // <video> that is being released.
  const handleLoadedData = useCallback(() => {
    if (!mountVideo) return
    setOpaquePlaybackUrl(playbackUrl)
  }, [mountVideo, playbackUrl])

  const handleError = useCallback(() => {
    if (retryVideoUrl) return
    setRetryVideoUrl(appendVideoRetryParam(videoUrl))
    setIsPlaying(false)
  }, [retryVideoUrl, videoUrl])

  const handleEnded = useCallback(() => {
    setIsPlaying(false)
    setHasCompletedPlayback(true)
    onEnded?.()
  }, [onEnded])

  /* -- Play/Pause toggle -- */
  /**
   * The toggle itself, with no event handling, so both entry points share it.
   *
   * `manualPauseRef` is why this must live in here rather than be driven from
   * outside through the imperative handle: the hover effect clears that flag
   * and resumes playback whenever it re-runs, so a pause that does not set it
   * gets silently undone.
   */
  const analyticsProjectId = useOptionalProjectId()
  const analyticsNodeId = useNodeId()
  const togglePlayback = useCallback(() => {
    const video = videoRef.current
    if (!video) return
    // Explicit play click/tap only — hover previews and autoplay never reach here.
    if (!isPlaying && analyticsProjectId && analyticsNodeId) {
      trackInteraction('canvas_output_play_clicked', { project_id: analyticsProjectId, node_id: analyticsNodeId, media_type: 'video' })
    }

    if (isPlaying) {
      video.pause()
      setIsPlaying(false)
      manualPauseRef.current = true
    } else {
      if (hasCompletedPlayback) {
        video.currentTime = 0
        setHasCompletedPlayback(false)
      }
      video.play().catch(() => {})
      setIsPlaying(true)
      manualPauseRef.current = false
    }
  }, [isPlaying, hasCompletedPlayback, analyticsProjectId, analyticsNodeId])

  /** The play/pause button. Stops propagation, as it always has. */
  const togglePlayPause = useCallback(
    (e: ReactMouseEvent) => {
      e.stopPropagation()
      onPreviewSelect?.()
      if (onPreviewSelect) {
        previewSelectionLog({
          event: 'preview-select-requested',
          source: 'play-toggle',
          toggle_on_frame_click: toggleOnFrameClick,
        })
      }
      togglePlayback()
    },
    [onPreviewSelect, toggleOnFrameClick, togglePlayback]
  )

  /**
   * Click anywhere on the frame. Deliberately does NOT stop propagation: the
   * click must still reach the owning tile, which is what selects the variant.
   * So a frame click both plays/pauses and selects; the controls that must not
   * do either (scrub, star, mute/expand/download) each stop propagation
   * themselves and never reach here.
   */
  const handleFrameClick = useCallback(() => {
    if (!toggleOnFrameClick) return
    togglePlayback()
  }, [toggleOnFrameClick, togglePlayback])

  /* -- Volume toggle -- */
  const toggleMute = useCallback((e: ReactMouseEvent) => {
    e.stopPropagation()
    const video = videoRef.current
    if (!video) return
    const nextMuted = !video.muted
    video.muted = nextMuted
    if (!nextMuted) video.volume = 1
    setIsMuted(nextMuted)
    setVideoMutedPreference(videoUrl, nextMuted)
    mutePreferenceLog({
      event: 'mute-preference-updated',
      surface: 'canvas-preview',
      muted: nextMuted,
      volume: video.volume,
    })

    if (manualPauseRef.current) {
      mutePreferenceLog({
        event: 'audio-toggle-playback-skipped',
        surface: 'canvas-preview',
        reason: 'manual-pause',
      })
      return
    }

    // Audible hover playback may have been rejected because pointer movement
    // is not a browser user activation. This click is one, so retry playback
    // after applying the requested audio state instead of leaving a silent,
    // paused element behind a responsive-looking audio control.
    video.play().then(
      () => {
        if (!video.paused) setIsPlaying(true)
        mutePreferenceLog({
          event: 'audio-toggle-playback-resolved',
          surface: 'canvas-preview',
          muted: video.muted,
          volume: video.volume,
        })
      },
      (error) => {
        if (video.paused) setIsPlaying(false)
        mutePreferenceLog({
          event: 'audio-toggle-playback-rejected',
          surface: 'canvas-preview',
          muted: video.muted,
          volume: video.volume,
          reason: error instanceof Error ? `${error.name}: ${error.message}` : String(error),
        })
      },
    )
  }, [videoUrl])

  /* -- Scrub bar -- */
  const scrubBarRef = useRef<HTMLDivElement>(null)

  const seekToPosition = useCallback((clientX: number) => {
    const video = videoRef.current
    const bar = scrubBarRef.current
    if (
      !video ||
      !bar ||
      !Number.isFinite(video.duration) ||
      video.duration <= 0
    )
      return

    const rect = bar.getBoundingClientRect()
    const ratio = Math.max(
      0,
      Math.min(1, (clientX - rect.left) / rect.width)
    )
    const newTime = ratio * video.duration
    video.currentTime = newTime
    setCurrentTime(newTime)

    if (newTime >= video.duration) {
      setHasCompletedPlayback(true)
      setIsPlaying(false)
      video.pause()
    }
  }, [])

  const handleScrubClick = useCallback(
    (e: ReactMouseEvent) => {
      e.stopPropagation()
      seekToPosition(e.clientX)
    },
    [seekToPosition]
  )

  const handleScrubDragStart = useCallback(
    (e: ReactMouseEvent) => {
      e.stopPropagation()
      e.preventDefault()
      isDraggingRef.current = true

      const wasPaused = videoRef.current?.paused ?? true
      videoRef.current?.pause()

      seekToPosition(e.clientX)

      const handleMouseMove = (moveEvent: globalThis.MouseEvent) => {
        seekToPosition(moveEvent.clientX)
      }

      const handleMouseUp = () => {
        isDraggingRef.current = false
        document.removeEventListener('mousemove', handleMouseMove)
        document.removeEventListener('mouseup', handleMouseUp)
        if (!wasPaused && !hasCompletedPlayback) {
          videoRef.current?.play().catch(() => {})
          setIsPlaying(true)
        }
      }

      document.addEventListener('mousemove', handleMouseMove)
      document.addEventListener('mouseup', handleMouseUp)
    },
    [seekToPosition, hasCompletedPlayback]
  )

  const progress = duration > 0 ? currentTime / duration : 0

  // Mute lives in the single top-right cluster for EVERY consumer. It is a
  // property of playing a video, not of any one node type, so there is no
  // placement switch: wherever this player renders, the control is in the same
  // corner, in the same order.
  const muteButton = (
    <button
      type="button"
      className="w-7 h-7 rounded bg-black/60 hover:bg-black/80 text-white flex items-center justify-center backdrop-blur-sm transition-colors nodrag"
      onClick={toggleMute}
      onPointerDown={stopParentDrag}
      title={isMuted ? t('videoEditorNode.unmute') : t('videoEditorNode.mute')}
      aria-label={isMuted ? t('videoEditorNode.unmute') : t('videoEditorNode.mute')}
      data-testid="video-volume-toggle"
    >
      {isMuted ? (
        <svg
          className="w-4 h-4"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z"
          />
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M17 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2"
          />
        </svg>
      ) : (
        <svg
          className="w-4 h-4"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M15.536 8.464a5 5 0 010 7.072M18.364 5.636a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z"
          />
        </svg>
      )}
    </button>
  )
  // No `hasOverlayButtons` gate any more: mute is always present, so the
  // cluster always renders.
  const overlayFlexDirection = overlayDirection === 'column' ? 'flex-col-reverse' : 'flex-row'

  return (
    <div
      ref={posterHostRef}
      className="relative w-full h-full"
      data-testid="video-preview-player"
      onClick={toggleOnFrameClick ? handleFrameClick : undefined}
    >
      {/* Persistent base layer: the cheap first-frame poster (RFC-0931). It
          stays mounted so the <video> can crossfade over it on hover and back
          out on release — a dissolve, not a hard cut. */}
      {usePoster && !gatedPosterUrl && (
        // Queued behind the canvas image pool. Same box as the poster so the
        // crossfade geometry is unchanged when it lands.
        <div
          aria-hidden="true"
          className="absolute inset-0 w-full h-full bg-surface-1"
          data-testid="video-poster-queued"
        />
      )}
      {usePoster && gatedPosterUrl && (
        <img
          src={gatedPosterUrl}
          alt=""
          decoding="async"
          loading="lazy"
          className={`absolute inset-0 w-full h-full ${mediaFitClass}`}
          onError={() => {
            onPosterSettled()
            setPosterFailed(true)
          }}
          onLoad={(e) => {
            onPosterSettled()
            // Report poster dimensions so the aspect box is set BEFORE hover —
            // prevents an idle→hover reflow for videos with no stored aspect
            // ratio (RFC-0931 §3), matching the <video> onLoadedMetadata path.
            const img = e.currentTarget
            if (img.naturalWidth && img.naturalHeight) {
              onLoadedDimensions?.(img.naturalWidth, img.naturalHeight)
            }
          }}
          data-testid="video-poster-idle"
        />
      )}

      {renderVideo && (
        <video
          key={playbackUrl}
          ref={videoRef}
          src={playbackUrl}
          // NOTE: intentionally NO `poster` attribute. `crossOrigin="anonymous"`
          // (kept for canvas frame extraction) makes the browser fetch the
          // poster attribute in CORS mode; the poster URL is also loaded by the
          // idle base <img> below in no-cors mode, so the shared HTTP-cache entry
          // fails the CORS check and the poster fetch errors on prod
          // (net::ERR_FAILED). The persistent base <img> already shows the
          // identical first frame, so a poster attribute here is redundant — the
          // <video> just fades in over it once its own first frame is decoded.
          className={`absolute inset-0 w-full h-full ${mediaFitClass} transition-opacity motion-reduce:transition-none ${
            !usePoster || videoOpaque ? 'opacity-100' : 'opacity-0'
          }`}
          // No poster to dissolve against → show immediately (no regression for
          // not-yet-backfilled videos); otherwise fade over VIDEO_FADE_MS.
          style={usePoster ? { transitionDuration: `${VIDEO_FADE_MS}ms` } : undefined}
          crossOrigin="anonymous"
          muted={isMuted}
          playsInline
          preload={preload}
          onTimeUpdate={handleTimeUpdate}
          onLoadedMetadata={handleLoadedMetadata}
          onLoadedData={handleLoadedData}
          onError={handleError}
          onEnded={handleEnded}
        />
      )}

      {/* THE overlay cluster -- top-right, one per player.
          Order: [mute, ...extraControls, download, ...topTrailingControls].
          With `overlayDirection='column'` the same order runs bottom-to-top
          down the right edge, so the trailing control (the variant star) sits
          in the corner and mute lowest.

          Mute is unconditional: it belongs to playing a video, not to a
          particular node type. Everything is hover-gated except Download when
          `downloadAlwaysVisible` is set, which stays pinned as the stable
          rightmost control so it never shifts as the hover group appears
          (post 1579949463762351).

          Callers must NOT add a second absolutely-positioned cluster in this
          corner -- pass `extraControls` instead. Two clusters is what this
          consolidation removed. Expand lives at the end of the bottom row,
          not here. */}
      <div
        className={`absolute top-2 right-2 flex ${overlayFlexDirection} gap-1 z-10`}
        data-testid="video-overlay-buttons"
        data-direction={overlayDirection}
      >
        <div
          className={`flex ${overlayFlexDirection} gap-1 transition-opacity ${
            isHovering ? 'opacity-100' : 'opacity-0 pointer-events-none focus-within:opacity-100 focus-within:pointer-events-auto'
          }`}
          data-testid="video-overlay-buttons-hover-group"
        >
          {muteButton}
          {extraControls}
          {showDownload && onDownload && !downloadAlwaysVisible && (
            <button
              type="button"
              disabled={downloadPreparing}
              onClick={(e) => {
                e.stopPropagation()
                onDownload()
              }}
              onPointerDown={stopParentDrag}
              className="w-7 h-7 rounded bg-black/60 hover:bg-black/80 text-foreground flex items-center justify-center backdrop-blur-sm transition-colors disabled:cursor-wait disabled:opacity-50 disabled:hover:bg-black/60 nodrag"
              title={t('videoPreviewPlayer.download')}
              aria-label={t('videoPreviewPlayer.download')}
              data-testid="video-download-btn"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                />
              </svg>
            </button>
          )}
        </div>
        {showDownload && onDownload && downloadAlwaysVisible && (
          <button
            type="button"
            disabled={downloadPreparing}
            onClick={(e) => {
              e.stopPropagation()
              onDownload()
            }}
            onPointerDown={stopParentDrag}
            className="w-7 h-7 rounded bg-black/60 hover:bg-black/80 text-foreground flex items-center justify-center backdrop-blur-sm transition-colors disabled:cursor-wait disabled:opacity-50 disabled:hover:bg-black/60 nodrag"
            title={t('videoPreviewPlayer.download')}
            aria-label={t('videoPreviewPlayer.download')}
            data-testid="video-download-btn"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
              />
            </svg>
          </button>
        )}
        {topTrailingControls && (
          <div className={`flex ${overlayFlexDirection} gap-1`}>
            {topTrailingControls}
          </div>
        )}
      </div>

      {/* Bottom row -- a flex track, NOT a stacking context.
          Order: [play/pause, timer, scrub, timer, expand].

          The row itself is always laid out and always `pointer-events-none`,
          so the tile stays clickable through its empty space; the transport
          group opts back in while hovering. The row owns the whole bottom
          edge, so callers must NOT add an absolutely positioned control along
          it -- the variant star once did (#3511) and covered play/pause. */}
      <div
        className={`absolute bottom-0 left-0 right-0 px-2 py-1.5 flex items-center gap-1.5 pointer-events-none transition-all ${
          isHovering ? 'bg-gradient-to-t from-black/80 to-transparent' : ''
        }`}
        data-testid="video-bottom-row"
      >
        <div
          className={`flex-1 min-w-0 flex items-center gap-1.5 transition-all nopan nodrag nowheel ${
            isHovering
              ? 'opacity-100 pointer-events-auto'
              : 'opacity-0 pointer-events-none focus-within:opacity-100 focus-within:pointer-events-auto'
          }`}
          onPointerDown={stopParentDrag}
          onMouseDown={stopParentDrag}
          data-testid="video-player-controls"
        >
        <button
          type="button"
          className="w-6 h-6 flex items-center justify-center text-foreground hover:text-white transition-colors flex-shrink-0"
          onClick={togglePlayPause}
          title={isPlaying ? t('videoEditorNode.pause') : t('videoEditorNode.play')}
          aria-label={isPlaying ? t('videoEditorNode.pause') : t('videoEditorNode.play')}
          data-testid="video-play-pause-btn"
        >
          {isPlaying ? (
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
              <rect x="6" y="4" width="4" height="16" rx="1" />
              <rect x="14" y="4" width="4" height="16" rx="1" />
            </svg>
          ) : (
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
              <path d="M8 5v14l11-7z" />
            </svg>
          )}
        </button>
        {/* Current time */}
        <span
          className="text-xxs text-text-secondary tabular-nums flex-shrink-0 select-none"
          data-testid="video-current-time"
        >
          {formatPlaybackTime(currentTime, duration)}
        </span>

        {/* Scrub bar */}
        <div
          ref={scrubBarRef}
          className="flex-1 h-5 flex items-center cursor-pointer group/scrub nopan nodrag nowheel"
          onClick={handleScrubClick}
          onPointerDown={stopParentDrag}
          onMouseDown={handleScrubDragStart}
          data-testid="video-scrub-bar"
        >
          <div className="w-full h-1 bg-white/20 rounded-full relative group-hover/scrub:h-1.5 transition-all">
            <div
              className="absolute top-0 left-0 h-full bg-white/70 rounded-full"
              style={{ width: `${progress * 100}%` }}
            />
            <div
              className="absolute top-1/2 -translate-y-1/2 w-3 h-3 bg-white rounded-full shadow-md opacity-0 group-hover/scrub:opacity-100 transition-opacity"
              style={{
                left: `${progress * 100}%`,
                transform: `translateX(-50%) translateY(-50%)`,
              }}
              data-testid="video-scrub-playhead"
            />
          </div>
        </div>

        {/* Total duration */}
        <span
          className="text-xxs text-text-secondary tabular-nums flex-shrink-0 select-none"
          data-testid="video-duration"
        >
          {formatPlaybackTime(duration, duration)}
        </span>

        {showExpand && onExpand && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation()
              onExpand()
            }}
            className="w-6 h-6 flex items-center justify-center text-foreground hover:text-white transition-colors flex-shrink-0"
            title={t('videoPreviewPlayer.expand')}
            aria-label={t('videoPreviewPlayer.expand')}
            data-testid="video-expand-btn"
          >
            <Maximize2 className="w-4 h-4" />
          </button>
        )}
        </div>
      </div>
    </div>
  )
}))

VideoPreviewPlayer.displayName = 'VideoPreviewPlayer'
